import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { FaFilePdf, FaTimes, FaUpload } from 'react-icons/fa';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  atualizarContratoPj,
  criarContratoPj,
  listarAnexosContratoPj,
  listarPrestadoresPj,
  obterDetalhesPrestadorPj,
  removerAnexoContratoPj,
  uploadAnexosContratoPjLote,
} from '~/Services/Service';
import {
  PERCENTUAL_RETENCAO_DEFAULT,
  TIPO_REMUNERACAO_FIXO,
  TIPO_REMUNERACAO_HORA,
} from '../../Prestadores/utils/formatters';

const LIMITE_PDFS = 10;

// DW-0982 — Melhoria Modal Contrato: switch "Alterar valor de remuneração?" foi
// removido. Modalidade, valores e percentuais ficam sempre visíveis. Em CRIAR começam
// em defaults (Hora, vazios, 10/10/10). Em EDITAR o useEffect popula com o detalhe
// do contrato (última vigência registrada no range). Quando o contrato tem fechamentos
// (`tem_fechamentos=true`), a edição do contrato fica bloqueada.
//
// EXCEÇÃO — anexos: contrato com fechamento lançado continua aceitando novas
// digitalizações. Anexar PDF não altera contrato nem vigência (endpoint dedicado
// `juridico/controlepj/anexos`), então nesse modo o modal NÃO chama
// `contratos/atualizar` (que barra fechamento lançado no backend) e sobe só os PDFs.
// Todo o resto dos campos permanece read-only.
const vazio = {
  codigoPj: '',
  dataInicio: '',
  dataRescisao: '',
  anexos: [],
  tipoRemuneracao: TIPO_REMUNERACAO_HORA,
  valorHora: '',
  valorFixo: '',
  horasBase: '',
  percentual13: PERCENTUAL_RETENCAO_DEFAULT,
  percentual14: PERCENTUAL_RETENCAO_DEFAULT,
  percentualFundo: PERCENTUAL_RETENCAO_DEFAULT,
  // DW-0982 P2: quando true, a vigência do valor-hora vale a partir do mês SEGUINTE ao
  // mês de início do contrato. Default automático pelo dia (ver calcularMesSeguintePorData).
  vigenciaMesSeguinte: false,
};

const dataInput = valor => (valor ? String(valor).slice(0, 10) : '');
const normalizarData = valor => (valor ? String(valor).slice(0, 10) : '');

// DW-0982 P2: default do toggle "Mês competência" pela data de início — dia ≤ 15 vale no mês
// de início; dia ≥ 16 vale no mês seguinte (corte do dia 15 definido pelo time de Processos).
const calcularMesSeguintePorData = valor => {
  const dia = Number(String(valor || '').slice(8, 10));
  return dia >= 16;
};

const ModalAddContrato = ({ show, onHide, onSalvo, onAtualizado, contrato }) => {
  const [form, setForm] = useState(vazio);
  const [prestadores, setPrestadores] = useState([]);
  const [erros, setErros] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [removendo, setRemovendo] = useState(false);
  const [arrastando, setArrastando] = useState(false);
  const inputArquivoRef = useRef(null);
  const idRef = useRef(0);

  useEffect(() => {
    if (!show) return;
    // DW-0982 — Melhoria Modal Contrato: em EDITAR, `contrato` é o detalhe vindo de
    // `obterDetalhesContratoPj` — traz a última vigência no range + tem_fechamentos.
    // Cai em PERCENTUAL_RETENCAO_DEFAULT/Hora quando não há vigência no range.
    setForm(contrato ? {
      codigoPj: contrato.codigo_pj || '',
      dataInicio: dataInput(contrato.data_inicio),
      dataRescisao: dataInput(contrato.data_rescisao),
      anexos: [],
      tipoRemuneracao: contrato.tipo_remuneracao || TIPO_REMUNERACAO_HORA,
      valorHora: contrato.valor_hora != null ? Number(contrato.valor_hora) : '',
      valorFixo: contrato.valor_fixo != null ? Number(contrato.valor_fixo) : '',
      horasBase: contrato.horas_base != null ? Number(contrato.horas_base) : '',
      percentual13: contrato.percentual_13 != null ? Number(contrato.percentual_13) : PERCENTUAL_RETENCAO_DEFAULT,
      percentual14: contrato.percentual_14 != null ? Number(contrato.percentual_14) : PERCENTUAL_RETENCAO_DEFAULT,
      percentualFundo: contrato.percentual_fundo != null ? Number(contrato.percentual_fundo) : PERCENTUAL_RETENCAO_DEFAULT,
      vigenciaMesSeguinte: calcularMesSeguintePorData(dataInput(contrato.data_inicio)),
    } : vazio);
    setErros({});
    setRemovendo(false);
    listarPrestadoresPj()
      .then(lista => {
        const prestadoresAtivos = Array.isArray(lista) ? lista : [];
        const codigoPjContrato = contrato ? contrato.codigo_pj : null;
        if (codigoPjContrato && !prestadoresAtivos.some(p => p.codigo === codigoPjContrato)) {
          setPrestadores([
            ...prestadoresAtivos,
            { codigo: codigoPjContrato, razao_social: contrato.razao_social, cnpj: contrato.cnpj, status: false },
          ]);
          return;
        }
        setPrestadores(prestadoresAtivos);
      })
      .catch(() => setPrestadores([]));
    if (contrato) {
      listarAnexosContratoPj(contrato.codigo)
        .then(lista => setForm(atual => ({
          ...atual,
          anexos: (Array.isArray(lista) ? lista : []).map(a => ({
            tipo: 'persistido',
            codigoAnexo: a.codigo,
            nomeArquivo: a.nome_arquivo,
          })),
        })))
        .catch(() => {});
    }
  }, [show, contrato]);

  const prestadorOptions = useMemo(() => prestadores.map(prestador => ({
    value: prestador.codigo,
    label: `${prestador.razao_social} - ${prestador.cnpj}`,
  })), [prestadores]);

  const prestadorSelecionado = prestadorOptions.find(
    option => option.value === Number(form.codigoPj)
  ) || null;
  const editandoContratoAtivo = !!contrato && !contrato.data_rescisao;
  const podeAdicionar = form.anexos.length < LIMITE_PDFS;
  // DW-0982 — contrato com fechamento lançado não pode mais ser editado.
  const temFechamentos = !!(contrato && contrato.tem_fechamentos);
  const bloqueiaEdicaoContrato = !!(contrato && temFechamentos);
  const podeEditarRemuneracao = !temFechamentos;
  // Anexar digitalização segue liberado mesmo com fechamento lançado: é o único
  // campo editável nesse modo, e sobe pelo endpoint de anexos, sem tocar o contrato.
  const anexosNovos = form.anexos.filter(item => item.tipo === 'novo');
  const somenteAnexos = bloqueiaEdicaoContrato;

  const alterarPrestador = option => {
    setForm(atual => {
      const mesmoPj = option && Number(atual.codigoPj) === Number(option.value);
      return {
        ...atual,
        codigoPj: option ? option.value : '',
        anexos: mesmoPj ? atual.anexos : [],
      };
    });
    setErros(atuais => ({ ...atuais, codigoPj: null }));

    // DW-0982 — Item 5: em NOVO contrato, ao selecionar o PJ, pré-preenche modalidade,
    // valores e percentuais com a vigência ativa do prestador (mesmo dado que a edição
    // já usa via detalhe do contrato). Sem vigência ativa, não preenche.
    if (contrato || !option) return;
    obterDetalhesPrestadorPj(option.value)
      .then(detalhe => {
        if (!detalhe || !detalhe.tipo_remuneracao) return;
        setForm(atual => {
          // Ignora resposta atrasada se o usuário já trocou de prestador.
          if (Number(atual.codigoPj) !== Number(option.value)) return atual;
          return {
            ...atual,
            tipoRemuneracao: detalhe.tipo_remuneracao,
            valorHora: detalhe.valor_hora != null ? Number(detalhe.valor_hora) : '',
            valorFixo: detalhe.valor_fixo != null ? Number(detalhe.valor_fixo) : '',
            horasBase: detalhe.horas_base != null ? Number(detalhe.horas_base) : '',
            percentual13: detalhe.percentual_13 != null ? Number(detalhe.percentual_13) : atual.percentual13,
            percentual14: detalhe.percentual_14 != null ? Number(detalhe.percentual_14) : atual.percentual14,
            percentualFundo: detalhe.percentual_fundo != null ? Number(detalhe.percentual_fundo) : atual.percentualFundo,
          };
        });
      })
      .catch(() => {});
  };

  const alterar = event => {
    const { name, value } = event.target;
    setForm(atual => ({ ...atual, [name]: value }));
    setErros(atuais => ({ ...atuais, [name]: null }));
  };

  // DW-0982 P2: ao mudar a Data início, recalcula o default do toggle "Mês competência"
  // pelo dia (≤15 mês início / ≥16 mês seguinte). O usuário ainda pode sobrescrever no toggle.
  const alterarDataInicio = event => {
    const { value } = event.target;
    setForm(atual => ({
      ...atual,
      dataInicio: value,
      vigenciaMesSeguinte: calcularMesSeguintePorData(value),
    }));
    setErros(atuais => ({ ...atuais, dataInicio: null }));
  };

  const limparInputArquivo = () => {
    if (inputArquivoRef.current) inputArquivoRef.current.value = '';
  };

  const validarArquivo = arquivo => {
    if (!arquivo) return null;
    if (arquivo.type !== 'application/pdf' || !/\.pdf$/i.test(arquivo.name)) {
      return 'Envie apenas arquivos PDF.';
    }
    if (arquivo.size > 10 * 1024 * 1024) {
      return 'Arquivo excede o limite de 10 MB.';
    }
    return null;
  };

  const selecionarArquivos = arquivos => {
    const arquivosLista = Array.from(arquivos || []);
    limparInputArquivo();
    if (!arquivosLista.length) return;
    const erroArquivo = arquivosLista.map(validarArquivo).find(e => e);
    if (erroArquivo) {
      setErros(atuais => ({ ...atuais, arquivo: erroArquivo }));
      return;
    }
    if (form.anexos.length + arquivosLista.length > LIMITE_PDFS) {
      setErros(atuais => ({ ...atuais, arquivo: `Limite de ${LIMITE_PDFS} PDFs por contrato.` }));
      return;
    }
    const novos = arquivosLista.map(file => {
      idRef.current += 1;
      return { tipo: 'novo', id: idRef.current, file, nomeArquivo: file.name };
    });
    setForm(atual => ({ ...atual, anexos: [...atual.anexos, ...novos] }));
    setErros(atuais => ({ ...atuais, arquivo: null }));
  };

  const alterarArquivo = event => {
    selecionarArquivos(event.target.files);
  };

  const removerArquivo = async item => {
    if (item.tipo === 'novo' || !contrato) {
      setForm(atual => ({ ...atual, anexos: atual.anexos.filter(a => a !== item) }));
      setErros(atuais => ({ ...atuais, arquivo: null }));
      return;
    }
    const confirmado = window.confirm('Remover este PDF do contrato? Esta ação não poderá ser desfeita.');
    if (!confirmado) return;
    try {
      setRemovendo(true);
      await removerAnexoContratoPj({
        codigo_contrato: contrato.codigo,
        codigo_pj: Number(form.codigoPj),
        codigo_anexo: item.codigoAnexo,
      });
      setForm(atual => ({ ...atual, anexos: atual.anexos.filter(a => a.codigoAnexo !== item.codigoAnexo) }));
      toast.success('PDF removido do contrato.');
      if (onAtualizado) onAtualizado();
    } catch (err) {
      toast.error(err.message || 'Erro ao remover PDF do contrato.');
    } finally {
      setRemovendo(false);
    }
  };

  const abrirSeletorArquivo = () => {
    if (inputArquivoRef.current) inputArquivoRef.current.click();
  };

  const soltarArquivo = event => {
    event.preventDefault();
    setArrastando(false);
    if (event.dataTransfer.files) selecionarArquivos(event.dataTransfer.files);
  };

  const validar = () => {
    const novosErros = {};
    if (!form.codigoPj || Number(form.codigoPj) <= 0) novosErros.codigoPj = 'Prestador deve ser preenchido.';
    if (!form.dataInicio) novosErros.dataInicio = 'Data início deve ser preenchida.';
    if (contrato && contrato.data_rescisao && !form.dataRescisao) {
      novosErros.dataRescisao = 'Data rescisão deve ser preenchida.';
    }
    const dataInicio = normalizarData(form.dataInicio);
    const dataRescisao = normalizarData(form.dataRescisao);
    if (dataInicio && dataRescisao && dataRescisao < dataInicio) {
      novosErros.dataRescisao = 'Data rescisão deve ser maior ou igual à data de início.';
    }
    // DW-0982 — remuneração/percentuais só bloqueiam quando há fechamento lançado.
    if (podeEditarRemuneracao) {
      if (form.tipoRemuneracao === TIPO_REMUNERACAO_HORA) {
        if (!form.valorHora || Number(form.valorHora) <= 0) {
          novosErros.valorHora = 'Valor-hora deve ser maior que zero.';
        }
      } else {
        if (!form.valorFixo || Number(form.valorFixo) <= 0) {
          novosErros.valorFixo = 'Valor fixo deve ser maior que zero.';
        }
        if (!form.horasBase || Number(form.horasBase) <= 0) {
          novosErros.horasBase = 'Horas base deve ser maior que zero.';
        }
      }
      [['percentual13', '13ª NF'], ['percentual14', '14ª NF'], ['percentualFundo', 'Fundo']]
        .forEach(([campo, nome]) => {
          const valor = form[campo];
          if (valor === '' || valor == null || isNaN(Number(valor))) {
            novosErros[campo] = `Percentual ${nome} obrigatório.`;
          } else if (Number(valor) < 0 || Number(valor) > 100) {
            novosErros[campo] = `Percentual ${nome} deve estar entre 0 e 100.`;
          }
        });
    }
    setErros(novosErros);
    return !Object.keys(novosErros).length;
  };

  const enviarSomenteAnexos = async () => {
    if (!anexosNovos.length) {
      toast.warning('Selecione ao menos um PDF para anexar.');
      return;
    }
    setSalvando(true);
    try {
      await uploadAnexosContratoPjLote({
        codigoPj: Number(form.codigoPj),
        codigoContrato: contrato.codigo,
        arquivos: anexosNovos.map(item => item.file),
      });
      toast.success(anexosNovos.length > 1 ? 'PDFs anexados ao contrato.' : 'PDF anexado ao contrato.');
      onSalvo();
    } catch (err) {
      toast.error(err.message || 'Erro ao anexar PDF ao contrato.');
    } finally {
      setSalvando(false);
    }
  };

  const salvar = async () => {
    // Contrato com fechamento lançado: dados do contrato permanecem read-only e o
    // `contratos/atualizar` nem é chamado — sobe apenas a digitalização nova.
    if (somenteAnexos) {
      await enviarSomenteAnexos();
      return;
    }
    if (!validar()) return;
    setSalvando(true);
    let contratoSalvo = false;
    try {
      const novos = anexosNovos;
      const ehFixo = form.tipoRemuneracao === TIPO_REMUNERACAO_FIXO;
      // DW-0982 — backend cria nova vigência quando remuneração/percentuais mudam.
      // Quando há fechamento, o backend barra a edição do contrato.
      const incluiRemuneracao = podeEditarRemuneracao;
      const payload = {
        codigo_pj: Number(form.codigoPj),
        data_inicio: form.dataInicio,
        // DW-0982 P2: competência-alvo da vigência do valor-hora (mês início vs mês seguinte).
        vigencia_mes_seguinte: !!form.vigenciaMesSeguinte,
        tipo_remuneracao: incluiRemuneracao ? form.tipoRemuneracao : null,
        valor_hora: incluiRemuneracao && !ehFixo ? Number(form.valorHora) : null,
        valor_fixo: incluiRemuneracao && ehFixo ? Number(form.valorFixo) : null,
        horas_base: incluiRemuneracao && ehFixo ? Number(form.horasBase) : null,
        percentual_13: incluiRemuneracao ? Number(form.percentual13) : null,
        percentual_14: incluiRemuneracao ? Number(form.percentual14) : null,
        percentual_fundo: incluiRemuneracao ? Number(form.percentualFundo) : null,
      };
      let codigoContrato;
      if (contrato) {
        await atualizarContratoPj({
          ...payload,
          codigo: contrato.codigo,
          data_rescisao: contrato.data_rescisao ? form.dataRescisao : null,
        });
        codigoContrato = contrato.codigo;
      } else {
        const resp = await criarContratoPj(payload);
        codigoContrato = resp.codigo;
      }
      contratoSalvo = true;

      if (novos.length) {
        await uploadAnexosContratoPjLote({
          codigoPj: Number(form.codigoPj),
          codigoContrato,
          arquivos: novos.map(n => n.file),
        });
      }

      toast.success(contrato ? 'Contrato atualizado.' : 'Contrato cadastrado.');
      onSalvo();
    } catch (err) {
      toast.error(err.message || 'Erro ao salvar contrato.');
      // Se o contrato já foi salvo e a falha foi no upload dos PDFs, fecha mesmo assim
      // (reabrir o contrato para anexar) — evita criar contrato duplicado numa nova tentativa.
      if (contratoSalvo) onSalvo();
    } finally {
      setSalvando(false);
    }
  };

  const label = (texto, obrigatorio = false) => (
    <>
      {texto}
      {obrigatorio && <span className="hpj-required">*</span>}
    </>
  );

  return (
    <Modal show={show} onHide={onHide} centered className="hpj-modal hpj-modal-contrato">
      <Modal.Header closeButton>
        <Modal.Title>{contrato ? 'Editar Contrato' : 'Novo Contrato'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="hpj-form-section hpj-form-section--last">
          <div className="hpj-form-section-title">Dados do contrato</div>
          <Row>
            <Col md={contrato ? 4 : 8}>
              <Form.Group className={erros.codigoPj ? 'has-error' : ''}>
                <Form.Label>{label('Prestador PJ', true)}</Form.Label>
                <Select
                  classNamePrefix="hpj-user-select"
                  isClearable
                  options={prestadorOptions}
                  value={prestadorSelecionado}
                  onChange={alterarPrestador}
                  placeholder="Selecione um prestador ativo"
                  isDisabled={bloqueiaEdicaoContrato}
                />
                {erros.codigoPj && <div className="hpj-field-error">{erros.codigoPj}</div>}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className={erros.dataInicio ? 'has-error' : ''}>
                <Form.Label>{label('Data início', true)}</Form.Label>
                <Form.Control
                  type="date"
                  name="dataInicio"
                  value={form.dataInicio}
                  onChange={alterarDataInicio}
                  disabled={bloqueiaEdicaoContrato}
                />
                {erros.dataInicio && <div className="hpj-field-error">{erros.dataInicio}</div>}
              </Form.Group>
            </Col>
            {contrato && (
              <Col md={4}>
                <Form.Group className={erros.dataRescisao ? 'has-error' : ''}>
                  <Form.Label>Data rescisão</Form.Label>
                  <Form.Control
                    type="date"
                    name="dataRescisao"
                    value={form.dataRescisao}
                    onChange={alterar}
                    disabled={editandoContratoAtivo || bloqueiaEdicaoContrato}
                  />
                  {erros.dataRescisao && <div className="hpj-field-error">{erros.dataRescisao}</div>}
                </Form.Group>
              </Col>
            )}
          </Row>

          {/* DW-0982 P2: o toggle "Mês competência" decide se o valor-hora passa a valer no
              mês de início do contrato ou no mês seguinte. Afeta APENAS a vigência do
              valor-hora — a data legal (Data início) do contrato não muda. */}
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Mês competência (vigência do valor-hora)</Form.Label>
                <Form.Control
                  as="select"
                  value={form.vigenciaMesSeguinte ? 'seguinte' : 'inicio'}
                  onChange={event => {
                    const valor = event.target.value;
                    setForm(atual => ({ ...atual, vigenciaMesSeguinte: valor === 'seguinte' }));
                  }}
                  disabled={bloqueiaEdicaoContrato}
                >
                  <option value="inicio">Mês de início</option>
                  <option value="seguinte">Mês seguinte</option>
                </Form.Control>
                <Form.Text className="text-muted">
                  Define em qual competência o valor-hora começa a valer. Não altera a data do contrato.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {/* DW-0982 — Melhoria Modal Contrato: campos sempre visíveis (sem switch).
              Quando há fechamentos lançados, modalidade/valores/percentuais não
              podem ser alterados. */}
          <div className="hpj-form-divider" />
          <div className="hpj-form-section-title">Remuneração e percentuais</div>
          {temFechamentos && (
            <div className="hpj-aviso-inline" style={{ background: '#fff7e6', borderLeftColor: '#fa8c16' }}>
              Este contrato possui fechamentos lançados: os dados do contrato não podem ser
              alterados. A inclusão de novas digitalizações (PDFs) continua liberada.
            </div>
          )}

          <Form.Group>
            <Form.Label>{label('Modalidade', true)}</Form.Label>
            <div>
              <Form.Check
                inline
                type="radio"
                id="hpj-contrato-modalidade-hora"
                label="Por Hora"
                name="tipoRemuneracao"
                checked={form.tipoRemuneracao === TIPO_REMUNERACAO_HORA}
                disabled={!podeEditarRemuneracao}
                onChange={() => setForm(atual => ({
                  ...atual,
                  tipoRemuneracao: TIPO_REMUNERACAO_HORA,
                  valorFixo: '',
                  horasBase: '',
                }))}
              />
              <Form.Check
                inline
                type="radio"
                id="hpj-contrato-modalidade-fixo"
                label="Valor Fixo"
                name="tipoRemuneracao"
                checked={form.tipoRemuneracao === TIPO_REMUNERACAO_FIXO}
                disabled={!podeEditarRemuneracao}
                onChange={() => setForm(atual => ({
                  ...atual,
                  tipoRemuneracao: TIPO_REMUNERACAO_FIXO,
                  valorHora: '',
                }))}
              />
            </div>
          </Form.Group>
          {form.tipoRemuneracao === TIPO_REMUNERACAO_FIXO ? (
            <Row>
              <Col md={4}>
                <Form.Group className={erros.valorFixo ? 'has-error' : ''}>
                  <Form.Label>{label('Valor fixo (R$)', true)}</Form.Label>
                  <Form.Control
                    name="valorFixo"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={form.valorFixo}
                    onChange={alterar}
                    disabled={!podeEditarRemuneracao}
                  />
                  {erros.valorFixo && <div className="hpj-field-error">{erros.valorFixo}</div>}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className={erros.horasBase ? 'has-error' : ''}>
                  <Form.Label>{label('Horas base / mês', true)}</Form.Label>
                  <Form.Control
                    name="horasBase"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={form.horasBase}
                    onChange={alterar}
                    disabled={!podeEditarRemuneracao}
                  />
                  {erros.horasBase && <div className="hpj-field-error">{erros.horasBase}</div>}
                </Form.Group>
              </Col>
              <Col md={4}>
                {/* ADR-013: Valor Hora derivado (readonly) — Valor_Fixo / Horas_Base.
                    Backend recalcula no save (defesa contra payload alterado), aqui é só preview. */}
                <Form.Group>
                  <Form.Label>Valor-hora (R$)</Form.Label>
                  <Form.Control
                    type="text"
                    readOnly
                    plaintext={false}
                    value={
                      Number(form.valorFixo) > 0 && Number(form.horasBase) > 0
                        ? (Number(form.valorFixo) / Number(form.horasBase)).toFixed(2)
                        : ''
                    }
                    placeholder="—"
                    title="Calculado: Valor fixo ÷ Horas base"
                    style={{ background: '#f5f5f5' }}
                  />
                  <Form.Text className="text-muted">
                    Calculado: Valor fixo ÷ Horas base
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          ) : (
            <Row>
              <Col md={6}>
                <Form.Group className={erros.valorHora ? 'has-error' : ''}>
                  <Form.Label>{label('Valor-hora (R$)', true)}</Form.Label>
                  <Form.Control
                    name="valorHora"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={form.valorHora}
                    onChange={alterar}
                    disabled={!podeEditarRemuneracao}
                  />
                  {erros.valorHora && <div className="hpj-field-error">{erros.valorHora}</div>}
                </Form.Group>
              </Col>
            </Row>
          )}

          {/* DW-0982 — Melhoria Percentuais por Contrato: aplicados sobre Valor NF no fechamento. */}
          <div className="hpj-form-subtitle">Percentuais de retenção sobre o Valor NF</div>
          <Row>
            <Col md={4}>
              <Form.Group className={erros.percentual13 ? 'has-error' : ''}>
                <Form.Label>{label('13ª NF (%)', true)}</Form.Label>
                <Form.Control
                  name="percentual13"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.percentual13}
                  onChange={alterar}
                  disabled={!podeEditarRemuneracao}
                />
                {erros.percentual13 && <div className="hpj-field-error">{erros.percentual13}</div>}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className={erros.percentual14 ? 'has-error' : ''}>
                <Form.Label>{label('14ª NF (%)', true)}</Form.Label>
                <Form.Control
                  name="percentual14"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.percentual14}
                  onChange={alterar}
                  disabled={!podeEditarRemuneracao}
                />
                {erros.percentual14 && <div className="hpj-field-error">{erros.percentual14}</div>}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className={erros.percentualFundo ? 'has-error' : ''}>
                <Form.Label>{label('Fundo (%)', true)}</Form.Label>
                <Form.Control
                  name="percentualFundo"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.percentualFundo}
                  onChange={alterar}
                  disabled={!podeEditarRemuneracao}
                />
                {erros.percentualFundo && <div className="hpj-field-error">{erros.percentualFundo}</div>}
              </Form.Group>
            </Col>
          </Row>

          <div className="hpj-form-divider" />

          <Form.Group className={erros.arquivo ? 'has-error' : ''}>
            <Form.Label>{label(`PDFs do contrato (até ${LIMITE_PDFS})`)}</Form.Label>
            <input
              ref={inputArquivoRef}
              className="hpj-upload-input"
              type="file"
              accept="application/pdf,.pdf"
              multiple
              onChange={alterarArquivo}
            />
            {podeAdicionar && (
              <div
                className={`hpj-upload-zone ${arrastando ? 'hpj-upload-zone--active' : ''} ${erros.arquivo ? 'hpj-upload-zone--error' : ''}`}
                role="button"
                tabIndex={0}
                onClick={abrirSeletorArquivo}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    abrirSeletorArquivo();
                  }
                }}
                onDragOver={event => {
                  event.preventDefault();
                  setArrastando(true);
                }}
                onDragLeave={() => setArrastando(false)}
                onDrop={soltarArquivo}
              >
                <div className="hpj-upload-icon">
                  <FaUpload />
                </div>
                <div className="hpj-upload-copy">
                  <div className="hpj-upload-title">Arraste os PDFs aqui ou clique para selecionar</div>
                  <div className="hpj-upload-hint">Vários PDFs, até 10 MB cada. Sobem ao salvar.</div>
                </div>
              </div>
            )}
            {form.anexos.map(item => (
              <div className="hpj-file-item" key={item.tipo === 'persistido' ? `p${item.codigoAnexo}` : `n${item.id}`}>
                <div className="hpj-file-item-icon">
                  <FaFilePdf />
                </div>
                <div className="hpj-file-item-copy">
                  <div className="hpj-file-item-name">{item.nomeArquivo}</div>
                  <div className="hpj-file-item-meta">{item.tipo === 'persistido' ? 'PDF salvo' : 'Novo (sobe ao salvar)'}</div>
                </div>
                <button
                  type="button"
                  className="hpj-file-item-remove"
                  title={item.tipo === 'novo' ? 'Descartar arquivo' : 'Remover arquivo'}
                  aria-label={`Remover ${item.nomeArquivo}`}
                  onClick={() => removerArquivo(item)}
                  disabled={salvando || removendo || (bloqueiaEdicaoContrato && item.tipo === 'persistido')}
                >
                  {removendo ? '...' : <FaTimes />}
                </button>
              </div>
            ))}
            {erros.arquivo && <div className="hpj-field-error">{erros.arquivo}</div>}
          </Form.Group>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" className="hpj-btn" onClick={onHide} disabled={salvando || removendo}>
          Cancelar
        </Button>
        <Button
          className="hpj-btn hpj-btn-primary"
          onClick={salvar}
          disabled={salvando || removendo || (somenteAnexos && !anexosNovos.length)}
        >
          {salvando ? 'Salvando...' : somenteAnexos ? 'Anexar PDFs' : contrato ? 'Atualizar' : 'Salvar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalAddContrato;
